import type { DragEvent } from "react";
import { useState } from "react";
import { Check, CheckCircle2, GripVertical, Info, Pencil, X } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@monorepo-apps/ui/src/components/popover";
import { Skeleton } from "@monorepo-apps/ui/src/components/skeleton";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

import { useUpdateFeature } from "@/application/use-cases/features/use-features.queries";
import type { FeatureRow, FeatureTriageStatus } from "@/infrastructure/supabase/database.types";
import { DomainPill } from "@/presentation/components/roadmaps/domain-pill";
import { DOMAIN_COLOR, DOMAIN_COLOR_SOFT, type Domain } from "@/presentation/data/domains";
import { VERDICT_COLOR, type Verdict } from "@/presentation/data/roadmaps";

export interface FeatureCardProps {
  feature: FeatureRow;
  variant: "stack" | "placed";
  verdict?: Verdict;
  onDragStart: (e: DragEvent<HTMLDivElement>, feature: FeatureRow) => void;
  onInfo: (feature: FeatureRow) => void;
  onEdit?: (feature: FeatureRow) => void;
  onRemove?: (feature: FeatureRow) => void;
}

export function FeatureCard({
  feature,
  variant,
  verdict,
  onDragStart,
  onInfo,
  onEdit,
  onRemove,
}: FeatureCardProps) {
  const domain = feature.domain as Domain | null;
  const accent = domain ? DOMAIN_COLOR[domain] : "oklch(0.5 0.02 240)";
  const fill = domain ? DOMAIN_COLOR_SOFT[domain] : "oklch(0.96 0.005 240)";
  const ringColor = verdict ? VERDICT_COLOR[verdict] : null;

  return (
    <div
      data-slot="feature-card"
      data-variant={variant}
      draggable
      onDragStart={(e) => onDragStart(e, feature)}
      className="group relative cursor-grab rounded-md border border-black/5 px-2.5 py-2 transition-all active:cursor-grabbing active:opacity-60"
      style={{
        background: fill,
        borderLeft: `3px solid ${accent}`,
        boxShadow: ringColor
          ? `inset 0 0 0 1px ${ringColor}40, 0 1px 2px oklch(0 0 0 / 0.04)`
          : "0 1px 2px oklch(0 0 0 / 0.04)",
      }}
    >
      <FeatureCardActions
        onInfo={() => onInfo(feature)}
        onEdit={onEdit ? () => onEdit(feature) : undefined}
        onRemove={onRemove ? () => onRemove(feature) : undefined}
      />
      <div className="flex items-start gap-1.5">
        <GripVertical className="text-foreground/30 mt-0.5 size-3 shrink-0" />
        <div className="flex min-w-0 flex-col gap-1">
          {/* Title row reserves right-padding for FeatureCardActions (which is
              absolutely-positioned top-1 right-1 and only visible on hover).
              Width: 3 buttons × 20px + 2 × 2px gap + 4px right offset + a small
              breathing gap → ~16. Subtitle and meta below stay full-width. */}
          <div className="flex min-w-0 items-start gap-1 pr-16">
            <p className="text-foreground/90 min-w-0 flex-1 text-[11.5px] leading-tight font-medium">
              {feature.title}
            </p>
            {feature.review_acknowledged ? (
              <CheckCircle2
                className="mt-0.5 size-3.5 shrink-0 text-[oklch(0.48_0.14_170)]"
                aria-label="Read and confirmed"
              />
            ) : null}
          </div>
          {feature.subtitle ? (
            <p className="text-foreground/65 line-clamp-2 text-[10px] leading-snug">
              {feature.subtitle}
            </p>
          ) : null}
          <FeatureCardMeta feature={feature} domain={domain} />
        </div>
      </div>
    </div>
  );
}

export function FeatureCardSkeleton() {
  const accent = "oklch(0.88 0.008 240)";
  const fill = "oklch(0.97 0.004 240)";
  return (
    <div
      data-slot="feature-card-skeleton"
      className="rounded-md border border-black/5 px-2.5 py-2"
      style={{
        background: fill,
        borderLeft: `3px solid ${accent}`,
        boxShadow: "0 1px 2px oklch(0 0 0 / 0.04)",
      }}
    >
      <div className="flex flex-col gap-1.5 pl-[18px]">
        <Skeleton className="h-3 w-4/5" />
        <Skeleton className="h-2.5 w-3/5" />
        <Skeleton className="h-3 w-12 rounded" />
      </div>
    </div>
  );
}

function FeatureCardMeta({ feature, domain }: { feature: FeatureRow; domain: Domain | null }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {domain ? <DomainPill domain={domain} /> : null}
      <JourneyPill featureId={feature.id} journey={feature.journey} />
      <TriageStatusPill featureId={feature.id} triageStatus={feature.triage_status} />
      {feature.notes ? (
        <span className="text-foreground/55 text-[10px] leading-tight italic">{feature.notes}</span>
      ) : null}
    </div>
  );
}

const JOURNEY_OPTIONS = ["B2C", "B2B", "both"] as const;
type JourneyOption = (typeof JOURNEY_OPTIONS)[number];

const TRIAGE_OPTIONS: readonly FeatureTriageStatus[] = ["needs-info", "wont-do", "parked"];

const TRIAGE_LABEL: Record<FeatureTriageStatus, string> = {
  "needs-info": "Needs info",
  "wont-do": "Won't do",
  parked: "Parked",
};

/** Tailwind classes for each triage state. Keyed for legibility on the card. */
const TRIAGE_CLASS: Record<FeatureTriageStatus, string> = {
  "needs-info": "bg-[oklch(0.92_0.07_30)] text-[oklch(0.4_0.16_30)] hover:bg-[oklch(0.88_0.09_30)]",
  "wont-do":
    "bg-[oklch(0.88_0.005_240)] text-[oklch(0.45_0.01_240)] line-through hover:bg-[oklch(0.84_0.008_240)]",
  parked: "bg-[oklch(0.9_0.06_310)] text-[oklch(0.42_0.15_310)] hover:bg-[oklch(0.86_0.08_310)]",
};

function JourneyPill({ featureId, journey }: { featureId: string; journey: string | null }) {
  const [open, setOpen] = useState(false);
  const updateFeature = useUpdateFeature();
  const isSet = journey !== null && journey !== "";
  const displayLabel = isSet ? journey : "Set journey";

  function select(next: JourneyOption) {
    setOpen(false);
    if (next === journey) return;
    updateFeature.mutate({ id: featureId, patch: { journey: next } });
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-slot="journey-pill"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          title={isSet ? `Journey: ${journey}. Click to change.` : "Set journey"}
          className={cn(
            "nodrag font-data inline-flex items-center rounded px-1 py-0 text-[9px] font-medium tracking-wide uppercase transition-colors",
            isSet
              ? "text-foreground/85 bg-black/5 hover:bg-black/10"
              : "border-foreground/25 text-foreground/45 hover:border-foreground/50 hover:text-foreground/70 border border-dashed"
          )}
        >
          {displayLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="!w-auto min-w-[120px] !p-1"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <ul className="flex flex-col">
          {JOURNEY_OPTIONS.map((option) => {
            const active = option === journey;
            return (
              <li key={option}>
                <button
                  type="button"
                  onClick={() => select(option)}
                  className={cn(
                    "font-data flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-[11px] tracking-wide uppercase transition-colors",
                    active
                      ? "bg-[oklch(0.55_0.17_190_/_0.12)] text-[oklch(0.42_0.18_195)]"
                      : "hover:bg-foreground/5 text-foreground/85"
                  )}
                >
                  <span>{option}</span>
                  {active ? <Check className="size-3" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Per-feature triage badge set during scope-triage meetings (needs-info,
 * wont-do, parked). Independent of the verdict system. NULL = unassigned;
 * renders as a dashed "Set status" affordance.
 */
function TriageStatusPill({
  featureId,
  triageStatus,
}: {
  featureId: string;
  triageStatus: FeatureTriageStatus | null;
}) {
  const [open, setOpen] = useState(false);
  const updateFeature = useUpdateFeature();

  function select(next: FeatureTriageStatus | null) {
    setOpen(false);
    if (next === triageStatus) return;
    updateFeature.mutate({ id: featureId, patch: { triage_status: next } });
  }

  const displayLabel = triageStatus ? TRIAGE_LABEL[triageStatus] : "Set status";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-slot="triage-status-pill"
          data-triage={triageStatus ?? "unassigned"}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          title={
            triageStatus
              ? `Triage: ${TRIAGE_LABEL[triageStatus]}. Click to change.`
              : "Set triage status"
          }
          className={cn(
            "nodrag font-data inline-flex items-center rounded px-1 py-0 text-[9px] font-medium tracking-wide uppercase transition-colors",
            triageStatus
              ? TRIAGE_CLASS[triageStatus]
              : "border-foreground/25 text-foreground/45 hover:border-foreground/50 hover:text-foreground/70 border border-dashed"
          )}
        >
          {displayLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="!w-auto min-w-[140px] !p-1"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <ul className="flex flex-col">
          {TRIAGE_OPTIONS.map((option) => {
            const active = option === triageStatus;
            return (
              <li key={option}>
                <button
                  type="button"
                  onClick={() => select(option)}
                  className={cn(
                    "font-data flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-[11px] tracking-wide uppercase transition-colors",
                    active
                      ? "bg-[oklch(0.55_0.17_190_/_0.12)] text-[oklch(0.42_0.18_195)]"
                      : "hover:bg-foreground/5 text-foreground/85"
                  )}
                >
                  <span>{TRIAGE_LABEL[option]}</span>
                  {active ? <Check className="size-3" /> : null}
                </button>
              </li>
            );
          })}
          {triageStatus ? (
            <li className="border-foreground/10 mt-1 border-t pt-1">
              <button
                type="button"
                onClick={() => select(null)}
                className="text-foreground/60 hover:bg-foreground/5 font-data flex w-full items-center gap-2 rounded px-2 py-1 text-left text-[10px] tracking-wide uppercase"
              >
                Clear status
              </button>
            </li>
          ) : null}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function FeatureCardActions({
  onInfo,
  onEdit,
  onRemove,
}: {
  onInfo: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
}) {
  return (
    <div
      // Y position picked to center the 20px (size-5) buttons on the same
      // line as the 14px (size-3.5) "read" CheckCircle2 icon: card py-2 (8px)
      // + check icon's mt-0.5 (2px) + half of 14px = 17px center. Button top
      // = 17 - half of 20 = 7px.
      className="absolute top-[7px] right-1 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onInfo();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        title="Feature detail"
        className="text-foreground/50 hover:text-foreground hover:bg-foreground/5 inline-flex size-5 items-center justify-center rounded"
      >
        <Info className="size-3" />
      </button>
      {onEdit ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          title="Edit verdict"
          className="text-foreground/50 hover:text-foreground hover:bg-foreground/5 inline-flex size-5 items-center justify-center rounded"
        >
          <Pencil className="size-3" />
        </button>
      ) : null}
      {onRemove ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          title="Send back to stack"
          className="text-foreground/50 hover:text-foreground hover:bg-foreground/5 inline-flex size-5 items-center justify-center rounded"
        >
          <X className="size-3" />
        </button>
      ) : null}
    </div>
  );
}
